import React, { useEffect, useState } from 'react';
import './DeliverySlots.scss';
import { FxTime, http, formatService, AlertService, FxInput } from '../../fx-core';
import { Dialog, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Fab, Select, MenuItem } from '@material-ui/core';
import _ from "lodash";
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';


type DeliverySlot = {
  id: number;
  description: string | null;
  startTime: string | null;
  endTime: string | null;
  stallId: number;
  slotType: number;
  slotDay: number;
  status?: number;
};

const DeliverySlots = (props: { stallId: number; handleDeliverySlotsClose: () => void }) => {

  const defaultContext = { startTime: "", endTime: "" };
  const [currentContext, setCurrentContext] = useState(defaultContext);
  const [deliverySlots, setDeliverySlots] = useState<DeliverySlot[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [stallDeliverySlotId, setStallDeliverySlotId] = useState<number>(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const handleCurrentContextItemChange = (data: { name: string; value: any }) => {
    setCurrentContext(prev => ({ ...prev, [data.name]: data.value }));
  };

  const handleInputChange = (data: { name: string; value: any }, item: DeliverySlot) => {
    if (data.name === 'startTime') {
      item.startTime = data.value;
    } else if (data.name === 'endTime') {
      item.endTime = data.value;
    } else if (data.name === 'description') {
      item.description = data.value;
    } else if (data.name === 'slotDay') {
      item.slotDay = data.value;
    }
    // Clone to avoid in-place state mutation issues
    setDeliverySlots(prev => [...prev]);
    handleCurrentContextItemChange(data);
  };

  const getStallDeliverySlots = () => {
    const inputData = { stallId: props.stallId, slotType: 0 };
    const apiOptions: any = { url: `stalldeliveryslot/query`, data: inputData };

    http.post(apiOptions).then(res => {
      const result: DeliverySlot[] = res?.data || [];
      for (let item of result) {
        item.startTime = item.startTime ? formatService.getConvertedTimeForDisplay(item.startTime) : null;
        item.endTime = item.endTime ? formatService.getConvertedTimeForDisplay(item.endTime) : null;
        item.slotType = item.slotType || 0;
        if (item.slotDay === null || item.slotDay === undefined) item.slotDay = 0;
      }
      // IMPORTANT: Do not auto-add an empty row on fetch
      setDeliverySlots(result);
      // console.log("deliveryslots", result);
    });
  };

  const addNewStallDeliverySlot = () => {
    setDeliverySlots(prev => [
      ...prev,
      { id: 0, description: null, startTime: null, endTime: null, stallId: props.stallId, slotType: 0, slotDay: 0 }
    ]);
  };

  const saveStallDeliverySlots = () => {
    const hasEmptyVal = _.filter(deliverySlots, d => !d.startTime || !d.endTime);
    if (hasEmptyVal.length > 0) {
      AlertService.showErrorMsg("Start and End time are required.");
      return;
    }

    const toSave = deliverySlots.map(item => {
      const copy = { ...item };
      copy.stallId = props.stallId;
      copy.startTime = copy.startTime ? formatService.getConvertedTimeForServer(copy.startTime) : null;
      copy.endTime = copy.endTime ? formatService.getConvertedTimeForServer(copy.endTime) : null;
      copy.slotType = copy.slotType || 0;
      copy.slotDay = copy.slotDay ?? 0;
      return copy;
    });

    const inputData = { stallDeliverySlotList: toSave };
    const apiOptions: any = { url: `stalldeliveryslot/save`, data: inputData };

    http.post(apiOptions).then(() => {
      saveStallDeliverySlotsCallback();
    });
  };

  const saveStallDeliverySlotsCallback = () => {
    AlertService.showSuccessMsg();
    props.handleDeliverySlotsClose();
  };

  const confirmDelete = (id: number, index: number) => {
    setStallDeliverySlotId(id);
    setDeleteIndex(index);
    setConfirmOpen(true);
    setMessage("Are you sure to delete this slot?");
  };

  const handleDialogClose = () => setConfirmOpen(false);

  const onConfirmCallback = () => {
    setConfirmOpen(false);
    deleteDeliverySlot();
  };

  const handleClose = () => props.handleDeliverySlotsClose();

  const deleteDeliverySlot = () => {
    // If it's an unsaved (local-only) row (id===0), just remove from state
    if (stallDeliverySlotId === 0 && deleteIndex !== null) {
      setDeliverySlots(prev => prev.filter((_, i) => i !== deleteIndex));
      AlertService.showSuccessMsg();
      return;
    }

    const apiOptions: any = { url: `stalldeliveryslot/${stallDeliverySlotId}` };
    http.delete(apiOptions).then(() => {
      AlertService.showSuccessMsg();
      getStallDeliverySlots();
    });
  };

  useEffect(() => {
    getStallDeliverySlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='deliveryslots-ui'>
      <div className='row title-font py-1 justify-content-between px-2 drawer-header'>
        <span className="col-11">
          <span>Delivery Slots</span>
        </span>
        <span className="col-1 text-center">
          <i className="fas fa-times-circle font-16 pointer" onClick={handleClose}></i>
        </span>
      </div>

      <div className='drawer-combo pt-3'>
        <span className='d-flex justify-content-end mr-4 pr-1 pb-3'>
          <Fab
            variant="extended"
            size="medium"
            color="primary"
            onClick={addNewStallDeliverySlot}
          >
            <i className="fas fa-plus pr-2"></i>
            Add
          </Fab>
        </span>
      </div>

      <div className='px-3 py-3 pt-4'>
        <div className='my-4 pb-5 pt-5 delivery-slot-scroll'>
          <TableContainer component={Paper}>
            <Table stickyHeader size="small" className='table-striped'>
              <TableHead>
                <TableRow>
                  <TableCell>Slot Day</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              {/* SINGLE TableBody with stable keys */}
              <TableBody>
                {deliverySlots.map((row, i) => (
                  row.status !== 2 && (
                    <TableRow key={row.id || `temp-${i}`}>
                      <TableCell>
                        <Select
                          name="slotDay"
                          value={row.slotDay ?? 0}
                          onChange={(e) => handleInputChange({ name: "slotDay", value: parseInt(String(e.target.value), 10) }, row)}
                          variant="outlined"
                          size="small"
                          fullWidth
                        >
                          <MenuItem value={0}>Sunday</MenuItem>
                          <MenuItem value={1}>Monday</MenuItem>
                          <MenuItem value={2}>Tuesday</MenuItem>
                          <MenuItem value={3}>Wednesday</MenuItem>
                          <MenuItem value={4}>Thursday</MenuItem>
                          <MenuItem value={5}>Friday</MenuItem>
                          <MenuItem value={6}>Saturday</MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell>
                        <FxInput
                          name="description"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={row.description}
                          onValueChange={(e: any) => handleInputChange(e, row)}
                        />
                      </TableCell>

                      <TableCell className='pt-0'>
                        <FxTime
                          name="startTime"
                          variant="outlined"
                          value={row.startTime}
                          onValueChange={(e: any) => handleInputChange(e, row)}
                          className="timestyle"
                        />
                      </TableCell>

                      <TableCell className='pt-0'>
                        <FxTime
                          name="endTime"
                          variant="outlined"
                          value={row.endTime}
                          onValueChange={(e: any) => handleInputChange(e, row)}
                          className="timestyle"
                        />
                      </TableCell>

                      <TableCell>
                        <span className='float-right'>
                          {/* allow delete for both saved and unsaved rows */}
                          <i
                            className="fas fa-trash-alt font-13"
                            onClick={() => confirmDelete(row.id, i)}
                          ></i>
                        </span>
                      </TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      <div className='px-3 py-2 drawer-footer'>
        <Button color="primary" variant="contained" fullWidth className='btn-style' onClick={saveStallDeliverySlots}>
          <b>Save</b>
        </Button>
      </div>

      <Dialog
        open={confirmOpen}
        maxWidth="sm"
        fullWidth
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <ConfirmDialog message={message || ''} handleDialogClose={handleDialogClose} onConfirmCallback={onConfirmCallback} />
      </Dialog>
    </div>
  );
};

export default DeliverySlots;
